// /var/www/oszczedzam/client/src/components/calendar/FinancialForecast.js
import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Paper, Grid, Divider, 
  Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, CircularProgress, 
  Card, CardContent, Alert, FormControlLabel, Switch, useTheme
} from '@mui/material';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, BarChart, Bar
} from 'recharts';
import { useTransactions } from '../../hooks/useTransactions';
import moment from 'moment';
import 'moment/locale/pl';

// Ustaw język
moment.locale('pl');

function FinancialForecast({ numberOfMonths = 6 }) {
  const theme = useTheme();
  const { transactions, loading, error } = useTransactions();
  const [forecastData, setForecastData] = useState([]);
  const [balanceData, setBalanceData] = useState([]);
  const [includeRecurringOnly, setIncludeRecurringOnly] = useState(false);

  // Przygotuj dane do prognozy
  useEffect(() => {
    if (transactions && transactions.length > 0) {
      generateForecastData();
    }
  }, [transactions, includeRecurringOnly, numberOfMonths]);

  // Generuj dane prognozy
  const generateForecastData = () => {
    // Aktualna data
    const now = moment();
    // Data końcowa (X miesięcy w przyszłość)
    const endDate = moment().add(numberOfMonths, 'months');
    
    // Przygotuj dane miesięczne
    const monthlyData = [];
    // Saldo początkowe (bieżące)
    let currentBalance = calculateCurrentBalance();
    
    // Dla każdego miesiąca w zakresie prognozy
    let currentMonth = now.clone().startOf('month');
    
    while (currentMonth.isBefore(endDate)) {
      const month = currentMonth.format('MMMM YYYY');
      const monthKey = currentMonth.format('YYYY-MM');
      
      // Zbierz wszystkie przychody i wydatki dla danego miesiąca
      const monthTransactions = getTransactionsForMonth(currentMonth);
      
      const monthIncome = monthTransactions.income;
      const monthExpenses = monthTransactions.expenses;
      
      // Aktualizuj saldo
      currentBalance += (monthIncome + monthExpenses); // expenses są już ujemne
      
      // Dodaj dane do tablicy
      monthlyData.push({
        month,
        monthKey,
        income: monthIncome,
        expenses: Math.abs(monthExpenses), // Wartość bezwzględna dla lepszej wizualizacji
        balance: currentBalance
      });
      
      // Przejdź do następnego miesiąca
      currentMonth.add(1, 'months');
    }
    
    setForecastData(monthlyData);
    
    // Przygotuj dane do wykresu salda
    const balanceChartData = monthlyData.map(item => ({
      name: item.month,
      saldo: item.balance
    }));
    
    setBalanceData(balanceChartData);
  };

  // Oblicz bieżące saldo
  const calculateCurrentBalance = () => {
    return transactions.reduce((sum, transaction) => sum + transaction.amount, 0);
  };

  // Pobierz transakcje dla danego miesiąca (przyszłego)
  const getTransactionsForMonth = (monthDate) => {
    const currentDate = moment();
    const isCurrentMonth = monthDate.isSame(currentDate, 'month');
    
    let relevantTransactions = transactions;
    
    // Jeśli wybrano opcję tylko cykliczne
    if (includeRecurringOnly) {
      relevantTransactions = relevantTransactions.filter(t => t.isRecurring);
    }
    
    // Dla bieżącego miesiąca uwzględnij tylko transakcje przyszłe
    if (isCurrentMonth) {
      // Filtruj tylko transakcje, które jeszcze się nie odbyły
      relevantTransactions = relevantTransactions.filter(t => {
        const transactionDate = moment(t.date);
        return transactionDate.isAfter(currentDate);
      });
    } else {
      // Dla przyszłych miesięcy uwzględnij tylko transakcje cykliczne
      relevantTransactions = relevantTransactions.filter(t => t.isRecurring);
    }
    
    // Zsumuj przychody i wydatki
    const income = relevantTransactions
      .filter(t => t.amount > 0)
      .reduce((sum, t) => sum + t.amount, 0);
    
    const expenses = relevantTransactions
      .filter(t => t.amount < 0)
      .reduce((sum, t) => sum + t.amount, 0);
    
    return { income, expenses };
  };

  // Formatuj kwotę
  const formatAmount = (amount) => {
    return amount.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' });
  };

  // Custom tooltip dla wykresu
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper elevation={3} sx={{ p: 2, backgroundColor: 'white' }}>
          <Typography variant="subtitle2">{label}</Typography>
          {payload.map((entry, index) => (
            <Box key={`item-${index}`} sx={{ color: entry.color, display: 'flex', alignItems: 'center', my: 0.5 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: entry.color, mr: 1 }} />
              <Typography variant="body2">
                {entry.name}: {formatAmount(entry.value)}
              </Typography>
            </Box>
          ))}
        </Paper>
      );
    }
    return null;
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Prognoza finansowa</Typography>
        <FormControlLabel
          control={
            <Switch
              checked={includeRecurringOnly}
              onChange={(e) => setIncludeRecurringOnly(e.target.checked)}
              color="primary"
            />
          }
          label="Tylko transakcje cykliczne"
        />
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : forecastData.length === 0 ? (
        <Alert severity="info">
          Brak wystarczających danych do prognozy. Dodaj transakcje cykliczne, aby zobaczyć prognozę.
        </Alert>
      ) : (
        <Grid container spacing={4}>
          {/* Wykres salda */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>Prognozowane saldo</Typography>
            <Box sx={{ height: 300, mb: 3 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={balanceData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="saldo" 
                    stroke={theme.palette.primary.main} 
                    activeDot={{ r: 8 }} 
                    name="Saldo"
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Grid>
          
          {/* Wykres przychodów i wydatków */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>Przychody i wydatki</Typography>
            <Box sx={{ height: 300, mb: 3 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={forecastData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar 
                    dataKey="income" 
                    name="Przychody" 
                    fill={theme.palette.success.main} 
                  />
                  <Bar 
                    dataKey="expenses" 
                    name="Wydatki" 
                    fill={theme.palette.error.main} 
                  />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Grid>
          
          {/* Tabela prognoz */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>Szczegóły prognozy</Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Miesiąc</TableCell>
                    <TableCell align="right">Przychody</TableCell>
                    <TableCell align="right">Wydatki</TableCell>
                    <TableCell align="right">Saldo</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {forecastData.map((row) => (
                    <TableRow key={row.monthKey}>
                      <TableCell component="th" scope="row">
                        {row.month}
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'success.main' }}>
                        {formatAmount(row.income)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'error.main' }}>
                        {formatAmount(row.expenses)}
                      </TableCell>
                      <TableCell 
                        align="right" 
                        sx={{ 
                          color: row.balance >= 0 ? 'success.main' : 'error.main',
                          fontWeight: 'bold'
                        }}
                      >
                        {formatAmount(row.balance)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      )}
    </Paper>
  );
}

export default FinancialForecast;